import { useQuery } from '@apollo/client';
import { IItinerary } from 'common/types';
import { getMaxViaCount, getMinutes } from 'common/utils';
import { sortMethods } from 'contexts/international';
import {
  IInternationalContextResult,
  ISchedules,
  SchFaresMap,
} from 'list/international/types';
import GET_FLIGHT_CURATION from './curation.gql';

const RANKING_SIZE = 3;

export enum CurationLabel {
  MIN_DURATION = 'MIN_DURATION',
  MIN_PRICE = 'MIN_PRICE',
  NONSTOP_MIN_PRICE = 'NONSTOP_MIN_PRICE',
  TOP_BOOKING = 'TOP_BOOKING',
  TOP_CLICK = 'TOP_CLICK',
}

export interface IFlight {
  flightNumbers: string;
}

interface IData {
  bookingRankings: IFlight[];
  impRankings: IFlight[];
}

const getCurationFlightKey = (key: string) =>
  key
    .split('|')
    .map(itinerary =>
      itinerary
        .split('+')
        .map(segment => segment.slice(14))
        .join('+'),
    )
    .join('-');

const getCurationRouteKey = (itinerary: IItinerary[]) =>
  itinerary
    .map(({ departureAirport, arrivalAirport }) => [
      departureAirport,
      arrivalAirport,
    ])
    .flat()
    .join('-');

const getMinDurationSet = (
  scheduleKeys: string[],
  schedules: ISchedules[] | undefined,
) => {
  if (!schedules) return new Set();
  const sorted = scheduleKeys
    .map((key, index) => {
      const scheduleDetails = key
        .split('|')
        .map((id, index) => schedules[index]?.[id]);
      return {
        index,
        value: scheduleDetails.reduce(
          (sum, { journeyTime }) => sum + getMinutes(journeyTime),
          0,
        ),
      };
    })
    .sort((a, b) => a.value - b.value);
  return new Set(
    sorted
      .filter(({ value }) => value === sorted[0].value)
      .map(({ index }) => scheduleKeys[index]),
  );
};

const getMinPriceSet = (scheduleKeys: string[], fares: SchFaresMap) => {
  const sorted = scheduleKeys
    .map((key, index) => ({
      index,
      value: sortMethods.getLowestPrice(fares.get(key)!),
    }))
    .sort((a, b) => a.value - b.value);
  return new Set(
    sorted
      .filter(({ value }) => value === sorted[0].value)
      .map(({ index }) => scheduleKeys[index]),
  );
};

const getNonstopMinPriceSet = (
  scheduleKeys: string[],
  schedules: ISchedules[] | undefined,
  fares: SchFaresMap,
) => {
  if (!schedules) return new Set();
  const filtered = scheduleKeys.filter(key => {
    const scheduleDetails = key
      .split('|')
      .map((id, index) => schedules[index]?.[id]);
    return !getMaxViaCount(scheduleDetails);
  });
  const sorted = filtered
    .map((key, index) => ({
      index,
      value: sortMethods.getLowestPrice(fares.get(key)!),
    }))
    .sort((a, b) => a.value - b.value);
  return new Set(
    sorted
      .filter(({ value }) => value === sorted[0].value)
      .map(({ index }) => filtered[index]),
  );
};

export default function useCuration(
  itinerary: IItinerary[],
  { schedules, fares, schs }: IInternationalContextResult,
  completed: boolean,
) {
  const { data = { bookingRankings: [], impRankings: [] } } = useQuery<IData>(
    GET_FLIGHT_CURATION,
    { variables: { route: getCurationRouteKey(itinerary) } },
  );

  const { bookingRankings, impRankings } = data;

  const scheduleSet = new Set(schs.map(getCurationFlightKey));

  const curationSet = completed
    ? {
        bookingRankings: new Set(
          bookingRankings
            .filter(({ flightNumbers }) => scheduleSet.has(flightNumbers))
            .slice(0, RANKING_SIZE)
            .map(({ flightNumbers }) => flightNumbers),
        ),
        impRankings: new Set(
          impRankings
            .filter(({ flightNumbers }) => scheduleSet.has(flightNumbers))
            .slice(0, RANKING_SIZE)
            .map(({ flightNumbers }) => flightNumbers),
        ),
        minDuration: getMinDurationSet(schs, schedules),
        minPrice: getMinPriceSet(schs, fares),
        nonstopMinPrice: getNonstopMinPriceSet(schs, schedules, fares),
      }
    : {
        bookingRankings: new Set(),
        impRankings: new Set(),
        minDuration: new Set(),
        minPrice: new Set(),
        nonstopMinPrice: new Set(),
      };

  const isMinDuration = (key: string) => curationSet.minDuration.has(key);

  const isMinPrice = (key: string) => curationSet.minPrice.has(key);

  const isNonstopMinPrice = (key: string) =>
    curationSet.nonstopMinPrice.has(key);

  const isTopBooking = (key: string) =>
    curationSet.bookingRankings.has(getCurationFlightKey(key));

  const isTopClick = (key: string) =>
    curationSet.impRankings.has(getCurationFlightKey(key));

  const curationFuncMap = new Map([
    [CurationLabel.MIN_DURATION, isMinDuration],
    [CurationLabel.MIN_PRICE, isMinPrice],
    [CurationLabel.NONSTOP_MIN_PRICE, isNonstopMinPrice],
    [CurationLabel.TOP_BOOKING, isTopBooking],
    [CurationLabel.TOP_CLICK, isTopClick],
  ]);

  const getLabels = (key: string) =>
    new Set(
      Array.from(curationFuncMap)
        .filter(([, func]) => func(key))
        .map(([label]) => label),
    );

  return {
    getLabels,
    isCurationTarget: (key: string) => !!getLabels(key).size,
    isMinDuration,
    isMinPrice,
    isNonstopMinPrice,
    isTopBooking,
    isTopClick,
  };
}
